import React, { useState } from 'react';
import {
  BOT_BASE_URL,
  BOT_BASE_ALT,
  BOT_SPECTRAL_FILTERS,
  BOT_SPECTRAL_GLOWS,
  BOT_ROLE_VARIANT,
} from '../constants/federationFaces';

/**
 * BotIcon — SFUI-004 Rev 3 unified bot avatar.
 *
 * Single base image (default) · spectral overlays via CSS filter.
 * Personas with their OWN canonical face (e.g. The Recycler) can override
 * by passing a `src` prop — the spectral filter still applies on top.
 *
 * Props:
 *   variant : 'blue' | 'purple' | 'red' | 'gold' | 'magenta'   (default 'blue')
 *   role    : alternative — pass a role string ('aria', 'keys', 'agent-zero',
 *             'recycler', 'gumroad', etc.) and the canonical variant is looked up.
 *   src     : optional custom image URL (overrides the canonical bot base)
 *   size    : number in pixels (default 40)
 *   glow    : boolean — wrap with spectral-colored shadow (default true)
 *   alt     : accessible label (defaults to "NextXus Federation Bot")
 *   testId  : data-testid value
 *   onClick : optional click handler (renders as a button)
 *
 * Persona FACES (Rex, Aria, Xavier, Roger AI, Axiom, Agent Zero, Oracle) are
 * NOT bots — those use the canonical face images. BotIcon is for operational
 * helpers: tour guides, library lookups, commerce assistants, restricted
 * access markers, system processes.
 */
const resolveVariant = ({ variant, role }) => {
  if (variant && BOT_SPECTRAL_FILTERS[variant]) return variant;
  if (role) {
    const key = String(role).toLowerCase().replace(/\s+/g, '-');
    if (BOT_ROLE_VARIANT[key]) return BOT_ROLE_VARIANT[key];
  }
  return 'blue';
};

const BotIcon = ({
  variant,
  role,
  src,
  size = 40,
  glow = true,
  alt,
  testId,
  onClick,
  borderColor,
  style,
}) => {
  const [failed, setFailed] = useState(false);
  const resolved = resolveVariant({ variant, role });
  const filter = BOT_SPECTRAL_FILTERS[resolved];
  const shadow = glow ? BOT_SPECTRAL_GLOWS[resolved] : 'none';
  const imageSrc = src || BOT_BASE_URL;

  const baseStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    objectFit: 'cover',
    filter,
    boxShadow: shadow,
    border: borderColor ? `2px solid ${borderColor}` : '2px solid rgba(255,255,255,0.18)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    cursor: onClick ? 'pointer' : 'default',
    flexShrink: 0,
    ...(style || {}),
  };

  if (failed) {
    // Tiny CSS-only fallback so the UI never breaks
    return (
      <div
        data-testid={testId}
        aria-label={alt || BOT_BASE_ALT}
        onClick={onClick}
        style={{
          ...baseStyle,
          background:
            resolved === 'purple'  ? 'radial-gradient(circle, #a855f7, #1a0a2e)' :
            resolved === 'red'     ? 'radial-gradient(circle, #ff3366, #1a0a0a)' :
            resolved === 'gold'    ? 'radial-gradient(circle, #ffb53e, #2e1a0a)' :
            resolved === 'magenta' ? 'radial-gradient(circle, #ec4899, #2e0a1a)' :
            resolved === 'emerald' ? 'radial-gradient(circle, #10b981, #0a2e1a)' :
                                     'radial-gradient(circle, #00c8ff, #0a1e3a)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontFamily: 'monospace',
          fontWeight: 800,
          fontSize: `${Math.max(10, size * 0.34)}px`,
        }}
      >
        BOT
      </div>
    );
  }

  return (
    <img
      data-testid={testId}
      src={imageSrc}
      alt={alt || BOT_BASE_ALT}
      onClick={onClick}
      onError={() => setFailed(true)}
      loading="lazy"
      decoding="async"
      style={baseStyle}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.transform = 'scale(1.06)';
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.transform = 'scale(1)';
      }}
    />
  );
};

export default BotIcon;
